/**
 * What the setup interviewer has understood so far, as one typed document.
 *
 * The gatherer writes it with `interview` on every turn, beside the question
 * it asks next; the phone draws it as "What I have so far"; the newest one is
 * the brief the build reads. Eight slots, and one coverage rule that says
 * when the asking stops: every slot the sector needs is filled or skipped. A
 * count never decides it, so a person who says everything in one breath is
 * asked nothing, and a person who says little is asked until the setup can
 * stand. Plans: docs/plans/onboarding-interview-and-experts.md (the slots),
 * docs/plans/done/onboarding-two-phases.md (the one-call gatherer; the team slot
 * retired, because who works here is done in the app after Create).
 *
 * The order is the asking order, and it is the order that understands the
 * business first: what they do, what they track, what they do in a day, what
 * they want to see. Money comes after those, because it is one part of how
 * the business works and not the reason for the setup; a record may hold no
 * amount at all (an attendance register, a stock count, a visit log), and
 * `money.none` is how the interviewer says so without asking about it.
 *
 * Pure, and no zod. The server's tool schema (tools/setup-schema.ts) is the
 * wire contract and imports the constants below, so the two can never
 * disagree on the name of a slot or a sector.
 */

export const SETUP_SLOTS = ['does', 'name', 'things', 'actions', 'watch', 'money', 'volume', 'currency'] as const;
export type Slot = (typeof SETUP_SLOTS)[number];

/** The twelve sectors of docs/THE-BOOK-CATALOG.md, and `other` for a business none of them names. */
export const SECTOR_IDS = [
  'shop',
  'wholesale',
  'contractor',
  'transport',
  'factory',
  'farm',
  'food',
  'repair',
  'clinic',
  'school',
  'rentals',
  'services',
  'other',
] as const;
export type SectorId = (typeof SECTOR_IDS)[number];

/** What a noun the person named is in the app. `unsure` is a question the interviewer still owes. */
export const THING_KINDS = ['list', 'record', 'field', 'unsure'] as const;
export type ThingKind = (typeof THING_KINDS)[number];

export const VOLUMES = ['few', 'daily', 'busy'] as const;
export type Volume = (typeof VOLUMES)[number];

export const PICKS = ['one', 'many'] as const;
export type Pick = (typeof PICKS)[number];

/** A question an expert raised and the lead has not asked yet. */
export type OpenQuestion = {
  readonly slot: Slot;
  readonly question: string;
  /** Two to six chips; empty for a free answer. */
  readonly options: readonly string[];
  readonly pick: Pick;
  /** One line the person reads under the question: what their answer changes. */
  readonly why: string;
  /** Who raised it: a specialist's id, or `interviewer`. */
  readonly from: string;
  readonly blocking: boolean;
};

export type SetupUnderstanding = {
  readonly business: {
    readonly name: string | null;
    readonly sector: SectorId | null;
    /** One sentence of what they do, in their words. */
    readonly does: string;
    readonly currency: string | null;
    readonly language: string | null;
  };
  /**
   * The nouns they track. A `field` names the record or list it sits on in
   * `on`, in the person's words ("price" on "Daily Sales"; "usual rate" on
   * "Customers"), so the build never has to guess which row holds it.
   */
  readonly things: ReadonlyArray<{ readonly word: string; readonly kind: ThingKind; readonly on?: string; readonly note?: string }>;
  /**
   * How payment works, where it does. `none` is true when nothing the person
   * described involves paying or being paid, so the slot is filled with no
   * question asked and no amount field is owed; null until it can be read
   * from what they said.
   */
  readonly money: {
    readonly none: boolean | null;
    readonly in: readonly string[];
    readonly out: readonly string[];
    readonly partPayment: boolean | null;
    readonly credit: boolean | null;
    readonly modes: readonly string[];
  };
  /** The verbs of their day: sell, deliver, collect, pay staff, buy stock. Each is a record. */
  readonly actions: readonly string[];
  /** The numbers they want on top, in their words. */
  readonly watch: readonly string[];
  readonly volume: Volume | null;
  readonly answered: ReadonlyArray<{ readonly slot: Slot; readonly question: string; readonly answer: string }>;
  readonly skipped: readonly Slot[];
  readonly open: readonly OpenQuestion[];
};

export type Coverage = {
  /** The slots this sector needs, in asking order. */
  readonly needed: readonly Slot[];
  readonly filled: readonly Slot[];
  /** Needed, not filled, and left to the assistant on purpose. */
  readonly skipped: readonly Slot[];
  /** Needed and still open. Empty is complete. */
  readonly missing: readonly Slot[];
  readonly complete: boolean;
  readonly questionsLeft: number;
};

/** The legacy interviewer's cap. The harness decides by readiness (setup-readiness.ts); Phase 10 deletes this. */
export const MAX_SETUP_QUESTIONS = 10;

const EVERY: readonly Slot[] = [...SETUP_SLOTS];
const WITHOUT_VOLUME: readonly Slot[] = SETUP_SLOTS.filter((slot) => slot !== 'volume');

/**
 * Which slots each sector must fill before the build. Volume shapes a setup
 * only where a day holds many rows (the customer starts filled in, the form
 * opens on the amount); a clinic or a contractor writes a handful and is not
 * asked. `other` needs every slot, because nothing else is known about it.
 */
export const SECTOR_NEEDS: Readonly<Record<SectorId, readonly Slot[]>> = {
  shop: EVERY,
  wholesale: EVERY,
  contractor: WITHOUT_VOLUME,
  transport: EVERY,
  factory: EVERY,
  farm: WITHOUT_VOLUME,
  food: EVERY,
  repair: WITHOUT_VOLUME,
  clinic: WITHOUT_VOLUME,
  school: WITHOUT_VOLUME,
  rentals: WITHOUT_VOLUME,
  services: WITHOUT_VOLUME,
  other: EVERY,
};

export const emptyUnderstanding = (): SetupUnderstanding => ({
  business: { name: null, sector: null, does: '', currency: null, language: null },
  things: [],
  money: { none: null, in: [], out: [], partPayment: null, credit: null, modes: [] },
  actions: [],
  watch: [],
  volume: null,
  answered: [],
  skipped: [],
  open: [],
});

/** Whether one slot holds enough to build on. The rule per slot, and nothing about the sector. */
export const isSlotFilled = (understanding: SetupUnderstanding, slot: Slot): boolean => {
  switch (slot) {
    case 'does':
      return understanding.business.does.trim() !== '';
    case 'name':
      return understanding.business.name !== null && understanding.business.name.trim() !== '';
    case 'things':
      return understanding.things.length > 0 && understanding.things.every((thing) => thing.kind !== 'unsure');
    case 'money':
      // No money in what they described is a complete answer on its own; where
      // there is money, the flows and the part-payment answer are what the
      // build needs (a Balance, a Paid beside the amount).
      return (
        understanding.money.none === true ||
        ((understanding.money.in.length > 0 || understanding.money.out.length > 0) && understanding.money.partPayment !== null)
      );
    case 'actions':
      return understanding.actions.length > 0;
    case 'watch':
      return understanding.watch.length > 0;
    case 'volume':
      return understanding.volume !== null;
    case 'currency':
      return understanding.business.currency !== null && understanding.business.currency.trim() !== '';
  }
};

/**
 * The slots that can never be left to the assistant: a workspace is never
 * made without a name the person said or agreed to (r3 Setup, item 15; the
 * build once named it "My business"). Noted as skipped, it is still missing.
 */
export const NEVER_SKIPPED: readonly Slot[] = ['name'];

/** Whether the workspace has a name: the one slot every build needs before it runs. */
export const hasWorkspaceName = (understanding: SetupUnderstanding): boolean => isSlotFilled(understanding, 'name');

export const coverageOf = (understanding: SetupUnderstanding): Coverage => {
  const needed = SECTOR_NEEDS[understanding.business.sector ?? 'other'];
  const filled = needed.filter((slot) => isSlotFilled(understanding, slot));
  const skipped = needed.filter((slot) => !filled.includes(slot) && !NEVER_SKIPPED.includes(slot) && understanding.skipped.includes(slot));
  const missing = needed.filter((slot) => !filled.includes(slot) && !skipped.includes(slot));
  return {
    needed,
    filled,
    skipped,
    missing,
    complete: missing.length === 0,
    questionsLeft: Math.max(0, MAX_SETUP_QUESTIONS - understanding.answered.length),
  };
};

/** The next slot to ask about: the first missing one in SETUP_SLOTS order, which is the order that shapes the most. */
export const nextSlot = (coverage: Coverage): Slot | null => coverage.missing[0] ?? null;

/**
 * Whether a slot holds anything at all, filled or not. The card draws a row
 * for every slot with content, so an answer is always visible the moment it
 * is noted; `isSlotFilled` is the stricter rule the coverage counts, so the
 * interviewer still asks for the part a slot lacks.
 */
export const hasContent = (understanding: SetupUnderstanding, slot: Slot): boolean => {
  switch (slot) {
    case 'does':
      return understanding.business.does.trim() !== '';
    case 'name':
      return understanding.business.name !== null && understanding.business.name.trim() !== '';
    case 'things':
      return understanding.things.length > 0;
    case 'money':
      return (
        understanding.money.none !== null ||
        understanding.money.in.length > 0 ||
        understanding.money.out.length > 0 ||
        understanding.money.modes.length > 0 ||
        understanding.money.partPayment !== null ||
        understanding.money.credit !== null
      );
    case 'actions':
      return understanding.actions.length > 0;
    case 'watch':
      return understanding.watch.length > 0;
    case 'volume':
      return understanding.volume !== null;
    case 'currency':
      return understanding.business.currency !== null && understanding.business.currency.trim() !== '';
  }
};

/**
 * What a slot with content still lacks to count as filled, in words for the
 * model, or null when it is filled or empty. "How money comes in and goes
 * out" is a poor thing to say to a model that has just noted both; this says
 * the one part it has not.
 */
export const whatIsMissing = (understanding: SetupUnderstanding, slot: Slot): string | null => {
  if (isSlotFilled(understanding, slot) || !hasContent(understanding, slot)) return null;
  switch (slot) {
    case 'money':
      return understanding.money.in.length === 0 && understanding.money.out.length === 0
        ? 'what is paid or collected, if any money changes hands'
        : 'whether people pay in part or later (partPayment)';
    case 'things':
      return `which of ${understanding.things
        .filter((thing) => thing.kind === 'unsure')
        .map((thing) => thing.word)
        .join(', ')} is a list, a record or a field`;
    default:
      return null;
  }
};
